import { Controller } from "@hotwired/stimulus"
import { FetchRequest } from '@rails/request.js'
import { Modal } from "bootstrap"
import { I18n } from 'i18n-js'
import locales from '../locales.json'
import { 
  quote_request_warning_modal_task_path,
  quote_request_recipients_task_path,
  quote_request_file_groups_task_path,
  send_quote_request_task_path
} from "../routes"

export default class extends Controller {
  static targets = [
    "modal", "companySelect", "recipientsList", "recipientsContainer",
    "sendButton", "quoteRequestButton", "commentEditor", "successMessage",
    "successMessageText", "formContent", "fileGroupsSection", "fileGroupsContainer",
    "validitySection", "validityDays", "companySearchContainer"
  ]

  static values = { taskId: Number }
  static params = ["companyId", "companyEmail"]

  connect() {
    this.selectedRecipient = null
    this.selectedFileIds = new Set()
    this.fetchingRecipients = null
    this.i18n = new I18n(locales)
    this.i18n.locale = document.querySelector("meta[name='locale']")?.content || 'hu'

    this.setupCompanySelectionDelegation()
  }

  // Triggered by the Ajánlatkérés button via data-action="click->quote-request#openModal".
  // We do NOT use Bootstrap's data-bs-toggle on the button because that would cause the
  // outer #taskModal to close when clicked inside it.
  openModal(event) {
    event.preventDefault()
    if (!this.hasModalTarget) return

    const commentText = this.getCommentText()
    if (!this.hasValidComment(commentText)) {
      alert(this.i18n.t("tasks.quote_request_no_comment_hint"))
      return
    }

    this.resetForm()

    // Dim the outer #taskModal so the nested dialog is visually distinct.
    this._taskModalEl = document.getElementById('taskModal')
    this._taskModalEl?.classList.add('has-nested-modal')

    // Remove dimming when this modal closes.
    this.modalTarget.addEventListener('hidden.bs.modal', () => {
      this._taskModalEl?.classList.remove('has-nested-modal')
    }, { once: true })

    // Open without a second backdrop — the outer #taskModal backdrop already dims the
    // background, and a second backdrop at the same z-index (1050) would fight with it.
    // The modal itself gets z-index 1075 via CSS so it appears above #taskModal (1055).
    const modal = Modal.getOrCreateInstance(this.modalTarget, { backdrop: false })
    modal.show()
  }

  setupCompanySelectionDelegation() {
    if (!this.hasCompanySearchContainerTarget) return

    this.companySearchContainerTarget.addEventListener('click', (event) => {
      const companyRow = event.target.closest('td.clickable[data-action*="selectCompany"]') ||
                        event.target.closest('tr td.clickable')
      if (!companyRow) return

      event.preventDefault()
      event.stopPropagation()

      const companyId = companyRow.getAttribute('data-quote-request-company-id-param') ||
                       companyRow.closest('tr')?.querySelector('[data-quote-request-company-id-param]')?.getAttribute('data-quote-request-company-id-param')
      const companyEmail = companyRow.getAttribute('data-quote-request-company-email-param') ||
                          companyRow.closest('tr')?.querySelector('[data-quote-request-company-email-param]')?.getAttribute('data-quote-request-company-email-param') || ""

      if (companyId) {
        this.selectCompany({ params: { companyId, companyEmail }, target: companyRow })
      }
    })
  }

  selectCompany(event) {
    const { companyId, companyEmail = "" } = event.params

    if (this.companySelectTarget.value === companyId) return

    if (this.hasCompanySearchContainerTarget) {
      const prev = this.companySearchContainerTarget.querySelector('tr.table-active')
      if (prev) prev.classList.remove('table-active')
      event.target?.closest('tr')?.classList.add('table-active')
    }

    this.companySelectTarget.value = companyId
    this.updateCompanySelectOption(companyId, companyEmail, event.target?.textContent?.trim() || "")
    this.companySelectTarget.dispatchEvent(new Event('change', { bubbles: true }))
  }

  updateCompanySelectOption(companyId, companyEmail, textContent) {
    let option = this.companySelectTarget.querySelector(`option[value="${companyId}"]`)
    if (!option) {
      option = document.createElement('option')
      option.value = companyId
      option.textContent = textContent
      this.companySelectTarget.appendChild(option)
    }
    if (companyEmail) option.dataset.email = companyEmail
  }

  resetForm() {
    this.companySelectTarget.value = ""

    if (this.hasCompanySearchContainerTarget) {
      const selected = this.companySearchContainerTarget.querySelector('tr.table-active')
      if (selected) selected.classList.remove('table-active')
    }

    this.recipientsContainerTarget.innerHTML = ""
    this.recipientsListTarget.style.display = "none"
    this.fileGroupsContainerTarget.innerHTML = ""
    this.fileGroupsSectionTarget.style.display = "none"

    if (this.hasValiditySectionTarget) this.validitySectionTarget.style.display = "none"
    if (this.hasValidityDaysTarget) this.validityDaysTarget.value = "15"

    this.sendButtonTarget.disabled = true
    this.selectedRecipient = null
    this.selectedFileIds.clear()
    this.fetchingRecipients = null

    this.formContentTarget.style.display = "block"
    this.successMessageTarget.style.display = "none"

    const modalFooter = this.modalTarget.querySelector('.modal-footer')
    if (modalFooter) modalFooter.style.display = "flex"
  }

  async onCompanyChange(event) {
    const companyId = event.target.value
    this.resetRecipients()
    if (!companyId) return
    if (this.fetchingRecipients === companyId) return

    this.fetchingRecipients = companyId
    try {
      const data = await this.fetchRecipients(companyId)
      if (this.companySelectTarget.value === companyId) this.renderRecipients(data)
    } catch (_) {
      // Silently fail
    } finally {
      this.fetchingRecipients = null
    }
  }

  resetRecipients() {
    this.recipientsContainerTarget.innerHTML = ""
    this.recipientsListTarget.style.display = "none"
    this.fileGroupsContainerTarget.innerHTML = ""
    this.fileGroupsSectionTarget.style.display = "none"
    if (this.hasValiditySectionTarget) this.validitySectionTarget.style.display = "none"
    this.sendButtonTarget.disabled = true
    this.selectedRecipient = null
    this.selectedFileIds.clear()
  }

  async fetchRecipients(companyId) {
    const url = quote_request_recipients_task_path(this.taskIdValue, { company_id: companyId })
    const request = new FetchRequest("get", url, { responseKind: "json" })
    const response = await request.perform()
    if (!response.ok) throw new Error(`Failed to fetch recipients: ${response.status}`)
    return await response.json
  }

  renderRecipients(data) {
    const container = this.recipientsContainerTarget
    container.innerHTML = ""

    if (data.company_email?.trim()) {
      container.appendChild(this.createRecipientOption(
        "company", data.company_id,
        `${data.company_name} (${this.i18n.t("tasks.company_modal")})`,
        data.company_email
      ))
    }

    if (data.persons?.length > 0) {
      data.persons.forEach(person => {
        if (person.email?.trim()) {
          container.appendChild(this.createRecipientOption(
            "person", person.id,
            `${person.name}${person.relationship_type ? ` (${person.relationship_type})` : ''}`,
            person.email
          ))
        }
      })
    }

    if (container.children.length > 0) {
      this.recipientsListTarget.style.display = "block"
    } else {
      container.innerHTML = `<div class="list-group-item text-muted text-center">${this.i18n.t("tasks.quote_request_no_recipients")}</div>`
      this.recipientsListTarget.style.display = "block"
    }
  }

  createRecipientOption(type, id, label, email) {
    const div = document.createElement("div")
    div.className = "list-group-item list-group-item-action"
    div.style.cursor = "pointer"
    div.dataset.type = type
    div.dataset.id = id
    div.dataset.email = email

    div.innerHTML = `
      <div class="form-check">
        <input class="form-check-input" type="radio" name="recipient" id="recipient_${type}_${id}" value="${id}" data-type="${type}" data-email="${email}">
        <label class="form-check-label" for="recipient_${type}_${id}">
          ${label}
          ${email ? `<small class="text-muted d-block">${email}</small>` : ''}
        </label>
      </div>
    `

    const radio = div.querySelector('input[type="radio"]')
    div.addEventListener("click", (e) => {
      if (e.target.type !== "radio") {
        e.preventDefault()
        e.stopPropagation()
        radio.checked = true
        this.onRecipientSelect(radio)
      }
    })
    radio.addEventListener("change", (e) => {
      if (e.target.checked) this.onRecipientSelect(e.target)
    })

    return div
  }

  async onRecipientSelect(radio) {
    const companyId = this.companySelectTarget.value
    this.selectedRecipient = {
      type: radio.dataset.type,
      id: radio.value,
      email: radio.dataset.email,
      companyId
    }

    this.sendButtonTarget.disabled = !this.selectedRecipient?.email

    if (this.selectedRecipient?.email) {
      await this.loadFileGroups()
      if (this.hasValiditySectionTarget) this.validitySectionTarget.style.display = "block"
    }
  }

  async loadFileGroups() {
    try {
      const url = quote_request_file_groups_task_path(this.taskIdValue)
      const request = new FetchRequest("get", url, { responseKind: "html" })
      const response = await request.perform()
      if (response.ok) {
        this.fileGroupsContainerTarget.innerHTML = await response.text
        this.fileGroupsSectionTarget.style.display = "block"
        this.setupFileCardClickHandlers()
      }
    } catch (_) {
      // Silently fail
    }
  }

  setupFileCardClickHandlers() {
    this.fileGroupsContainerTarget.querySelectorAll('[data-file-id]').forEach(card => {
      if (card.dataset.clickHandlerSet) return
      card.addEventListener('click', (e) => {
        if (e.target.type === 'checkbox') return
        const checkbox = card.querySelector('input[type="checkbox"]')
        if (checkbox) {
          checkbox.checked = !checkbox.checked
          checkbox.dispatchEvent(new Event('change', { bubbles: true }))
        }
      })
      card.dataset.clickHandlerSet = 'true'
    })
  }

  onFileToggle(event) {
    const fileId = parseInt(event.target.dataset.fileId)
    if (event.target.checked) {
      this.selectedFileIds.add(fileId)
    } else {
      this.selectedFileIds.delete(fileId)
    }

    const card = event.target.closest('.card')
    if (card && this.modalTarget.contains(card)) {
      card.classList.toggle('border-primary', event.target.checked)
      card.style.borderWidth = event.target.checked ? '2px' : ''
    }
  }

  sendQuoteRequest() {
    if (!this.selectedRecipient?.email) return

    const mainModal = Modal.getInstance(this.modalTarget)
    if (!mainModal) { this.showWarningModal(); return }

    this.modalTarget.addEventListener('hidden.bs.modal', () => {
      this.showWarningModal()
    }, { once: true })
    mainModal.hide()
  }

  showWarningModal() {
    const url = quote_request_warning_modal_task_path(this.taskIdValue)
    const request = new FetchRequest("get", url, { responseKind: "html" })

    request.perform().then(response => {
      if (!response.ok) { this.actuallySendQuoteRequest(); return }
      return response.text
    }).then(html => {
      if (!html) return
      const existing = document.getElementById('quoteRequestWarningModal')
      if (existing) { Modal.getInstance(existing)?.dispose(); existing.remove() }
      document.body.insertAdjacentHTML('beforeend', html)
      this.initializeWarningModal()
    }).catch(() => this.actuallySendQuoteRequest())
  }

  initializeWarningModal() {
    const modalElement = document.getElementById('quoteRequestWarningModal')
    if (!modalElement) { this.actuallySendQuoteRequest(); return }

    modalElement.querySelector('[data-action*="confirmSendQuoteRequest"]')
      ?.addEventListener('click', (e) => {
        e.preventDefault(); e.stopPropagation()
        this.confirmSendQuoteRequest()
      }, { once: true })

    new Modal(modalElement, { backdrop: 'static', keyboard: false }).show()
  }

  confirmSendQuoteRequest() {
    const modalElement = document.getElementById('quoteRequestWarningModal')
    if (!modalElement) { this.actuallySendQuoteRequest(); return }

    const modal = Modal.getInstance(modalElement)
    if (!modal) { modalElement.remove(); this.actuallySendQuoteRequest(); return }

    modalElement.addEventListener('hidden.bs.modal', () => {
      modal.dispose(); modalElement.remove(); this.actuallySendQuoteRequest()
    }, { once: true })
    modal.hide()
  }

  actuallySendQuoteRequest() {
    if (!this.selectedRecipient?.email) return

    const commentText = this.getCommentText()
    if (!this.hasValidComment(commentText)) {
      alert(this.i18n.t("tasks.quote_request_no_comment"))
      return
    }

    const validityDays = this.hasValidityDaysTarget
      ? Math.max(3, Math.min(90, parseInt(this.validityDaysTarget.value) || 15))
      : 15

    const url = send_quote_request_task_path(this.taskIdValue)
    const request = new FetchRequest("post", url, {
      responseKind: "turbo-stream",
      body: JSON.stringify({
        recipient_type: this.selectedRecipient.type,
        recipient_id: this.selectedRecipient.id,
        recipient_email: this.selectedRecipient.email,
        company_id: this.selectedRecipient.companyId,
        comment: commentText,
        selected_file_ids: Array.from(this.selectedFileIds),
        validity_days: validityDays
      }),
      headers: {
        "Content-Type": "application/json",
        "Accept": "text/vnd.turbo-stream.html",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
      }
    })

    request.perform().then(response => {
      if (response.ok) {
        this.showSuccessModal(this.i18n.t("tasks.quote_request_sent"))
      } else {
        alert(this.i18n.t("tasks.quote_request_error"))
      }
    }).catch(error => {
      alert(this.i18n.t("tasks.quote_request_error_with_message", { message: error?.message || "" }))
    })
  }

  getCommentText() {
    if (!this.hasCommentEditorTarget) return ""

    const wrapper = this.commentEditorTarget
    const trixEditor = wrapper.querySelector('trix-editor') || wrapper

    if (trixEditor.inputElement?.value) return trixEditor.inputElement.value

    const inputId = trixEditor.getAttribute?.('input')
    if (inputId) {
      const hiddenInput = document.getElementById(inputId) ||
                         wrapper.querySelector('input[type="hidden"][name*="comment"]')
      if (hiddenInput?.value) return hiddenInput.value
    }

    if (trixEditor.editor) {
      try {
        const doc = trixEditor.editor.getDocument()
        if (doc && !doc.isEmpty) return doc.toString()
      } catch (_) {}
    }

    return wrapper.querySelector('input[type="hidden"][name*="comment"]')?.value || ""
  }

  hasValidComment(commentText) {
    if (!commentText?.trim()) return false
    const trimmed = commentText.trim()
    return !["<div><br></div>", "<div></div>", "<br>"].includes(trimmed)
  }

  showSuccessModal(message) {
    const modalElement = document.getElementById('quoteRequestSuccessModal')
    if (!modalElement) return

    const msgEl = modalElement.querySelector('[data-quote-request-success-message]')
    if (msgEl) msgEl.textContent = message

    Modal.getInstance(modalElement)?.dispose()
    const modal = new Modal(modalElement, { backdrop: true, keyboard: true, focus: true })
    modalElement.addEventListener('hidden.bs.modal', () => modal.dispose(), { once: true })
    modal.show()
  }
}
